/**
 * JSON Schema type definitions for OpenAPI
 */
interface JSONSchema {
  default?: unknown;
  enum?: unknown[];
  example?: unknown;
  format?: string;
  items?: JSONSchema;
  properties?: Record<string, JSONSchema>;
  required?: string[];
  type?: string;
}

/**
 * With a piece of data, determine what `type` it should be represented as in JSON Schema.
 */
function getJSONSchemaTypeFromData(
  data: unknown,
): 'array' | 'boolean' | 'integer' | 'number' | 'object' | 'string' | null {
  switch (typeof data) {
    case 'boolean':
      return 'boolean';
    case 'string':
      return 'string';
    case 'bigint':
      return 'integer';
    case 'number':
      return Number.isInteger(data) ? 'integer' : 'number';
    default:
      if (data === null) {
        return null;
      } else if (Array.isArray(data)) {
        return 'array';
      }
  }

  return 'object';
}

/**
 * NOTE:
 *
 * this function is a fork from packages/backend/openapi/manual/index.js
 * it has been migrated to TS to provide better type safety and maintainability
 * all references of the original function should be updated to use this function
 *
 * Transform a piece of JSON-parsed data into a JSON Schema schema for use in an OpenAPI response schema.
 * This is a pure function that generates schema definitions from example data.
 */
export function generateJSONSchemaFromObject(obj: unknown): JSONSchema {
  const schema: JSONSchema = {};
  const type = getJSONSchemaTypeFromData(obj);

  if (type === null) {
    // Since the value is null we can't really generate improved docs for it (is it a nullable string? an array?), we
    // should just ignore it.
  } else if (['boolean', 'integer', 'number', 'string'].includes(type)) {
    schema.type = type;
    schema.example = obj;

    // Don't bother adding defaults for strings since all we *really* have is an example.
    if (type === 'boolean') {
      schema.default = true;
    } else if (type === 'integer') {
      schema.default = 0;
    } else if (type === 'number') {
      schema.default = 0.0;
    }
  } else if (type === 'array') {
    schema.type = 'array';

    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        // For empty arrays, set items to empty object schema
        schema.items = { type: 'object', properties: {} };
      } else {
        const typeArray: string[] = [];
        obj.forEach(key => {
          const keyType = getJSONSchemaTypeFromData(key);
          if (keyType && typeArray.indexOf(keyType) === -1) {
            typeArray.push(keyType);
          }
        });

        if (typeArray.length === 1) {
          // Create a copy to avoid mutating the original array
          const objCopy = [...obj];
          schema.items = generateJSONSchemaFromObject(objCopy.shift());
        } else if (typeArray.length === 2 && typeArray.includes('number') && typeArray.includes('integer')) {
          // If we have an array of mixed numbers, we should prioritize the `number` over the `integer` as it's more
          // generic.
          let nbFirst: unknown = null;
          obj.forEach(nb => {
            if (getJSONSchemaTypeFromData(nb) === 'number' && nbFirst === null) {
              nbFirst = nb;
            }
          });

          schema.items = generateJSONSchemaFromObject(nbFirst);
        } else if (typeArray.length > 1) {
          // For arrays with multiple different types, use the first item as the schema
          schema.items = generateJSONSchemaFromObject(obj[0]);
        }
      }
    }
  } else if (type === 'object' && obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
    const properties: Record<string, JSONSchema> = {};
    Object.keys(obj).forEach(key => {
      // Chunk up the object and parse each individual part of its schema.
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        properties[key] = generateJSONSchemaFromObject((obj as Record<string, unknown>)[key]);
      }
    });

    schema.type = 'object';
    schema.properties = properties;
  }

  return schema;
}
